import environment from '../environment.json';

import AsyncAuthService from '../services/AsyncAuthService';
import AuthService, { AuthEvents } from '../services/AuthService';
import EnvironmentService from '../services/EnvironmentService';
import { Client, CompatClient, IMessage, Stomp, StompHeaders } from '@stomp/stompjs';
import SockJS from 'sockjs-client';

export class WebSocketClient {
  private stompClient!: Client;

  private headers: StompHeaders = {};
  private baseUrl: string;

  private activeSubscriptions: Array<{ channelName: string; channelId: string; handler: (s: IMessage) => void }> = [];

  private pendingMessages: Array<{ channelName: string; channelId: string; content: string }> = [];

  private reconnectDelay: number = 7000;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;

    this.initializeConnection();

    AuthService.subscribe(AuthEvents.LOGOUT, () => {
      this.deactivate();
    });

    AuthService.subscribe(AuthEvents.LOGIN, () => {
      this.initializeConnection();
    });
  }

  private async initializeConnection() {
    this.headers = await this.generateHeaders();

    this.stompClient = new Client({
      connectHeaders: this.headers,
      reconnectDelay: this.reconnectDelay,
      webSocketFactory: () => new SockJS(this.baseUrl),
    });

    this.stompClient.onConnect = this.onConnect;
    this.stompClient.activate();
  }

  private deactivate() {
    this.pendingMessages = [];
    this.activeSubscriptions = [];
    this.stompClient.deactivate({ force: true });
  }

  private onConnect = () => {
    this.processPendingMessages();

    this.activeSubscriptions.forEach((s) => {
      this.subscribeRaw(s.channelName, s.channelId, s.handler);
    });
  };

  /**
   * Subscribes to a specified channel with a string message handler.
   *
   * @param channelName The name of the channel to subscribe to.
   * @param channelId The ID of the channel to subscribe to.
   * @param handler The function to handle incoming string messages.
   */
  public subscribe(channelName: string, channelId: string, handler: (s: string) => void) {
    this.subscribeRaw(channelName, channelId, (s) => handler(s.body));
  }

  /**
   * Subscribes to a specified channel with a raw IMessage handler.
   *
   * @param channelName The name of the channel to subscribe to.
   * @param channelId The ID of the channel to subscribe to.
   * @param handler The function to handle incoming IMessage objects.
   */
  public subscribeRaw(channelName: string, channelId: string, handler: (s: IMessage) => void) {
    if (this.stompClient.connected) {
      this.stompClient.subscribe(this.getChannelUrl(channelName, channelId), handler, this.headers);
    }

    if (!this.activeSubscriptions.find((s) => s.channelId === channelId && s.channelName === channelName)) {
      this.activeSubscriptions.push({ channelName, channelId, handler });
    }
  }

  /**
   * Unsubscribes from a specified channel.
   *
   * @param channelName The name of the channel to unsubscribe from.
   * @param channelId The ID of the channel to unsubscribe from.
   */
  public unsubscribe(channelName: string, channelId: string) {
    if (this.stompClient.connected) {
      this.stompClient.unsubscribe(this.getChannelUrl(channelName, channelId));
    }

    this.activeSubscriptions = this.activeSubscriptions.filter((s) => s.channelId === channelId && s.channelName === channelName);
  }

  /**
   * Sends a message to a specified channel.
   *
   * @param channelName The name of the channel to send the message to.
   * @param channelId The ID of the channel to send the message to.
   * @param content The message content to send.
   */
  public sendMessage(channelName: string, channelId: string, content: string) {
    if (this.stompClient.connected) {
      this.stompClient.publish({
        headers: this.headers,
        destination: this.getChannelUrl(channelName, channelId),
        body: content,
      });
    } else {
      this.pendingMessages.push({ channelName, channelId, content });
    }
  }

  private processPendingMessages() {
    this.pendingMessages.forEach(({ channelName, channelId, content }) => {
      this.sendMessage(channelName, channelId, content);
    });
    this.pendingMessages = [];
  }

  private getChannelUrl(channelName: string, channelId: string) {
    return '/channel/' + channelName + '/' + channelId;
  }

  private async generateHeaders() {
    let token;

    if (EnvironmentService.usesAsyncStorageStrategy()) {
      token = await AsyncAuthService.getToken();
    } else {
      token = AuthService.getToken();
    }

    return {
      Authorization: `Bearer ${token}`,
    };
  }
}

export default new WebSocketClient(environment.baseURL + '/ws');
